import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";
import { InfiniteQueryResult, QueryResult } from "react-query";
import { AxiosError } from "axios";

import NavArrowIcon from "components/NavArrowIcon";
import { IconButton } from "fe-shared/src/components";

import * as api from "routes/TransactionDetails/modules/api";

import { ApiError } from "fe-shared/src/types/common";
import { getColor } from "fe-shared/src/helpers/colors";

import styles from "./TransactionNavigation.module.scss";

interface Props {
  navigationTransactions: InfiniteQueryResult<
    api.NavigationTransactions,
    AxiosError<ApiError>
  >;
  transactionListInfo: QueryResult<
    api.TransactionListInfo,
    AxiosError<ApiError>
  >;
  isDirectNavigation: boolean;
}

const TransactionNavigation = ({ navigationTransactions, transactionListInfo: transactionListInfo, isDirectNavigation }: Props) => {
  const{
    data: transactionListInfoResult,
    isLoading: transactionListInfoResultInProgress,
  } = transactionListInfo;
  const {
    data,
    canFetchMore,
    fetchMore,
    isFetchingMore,
  } = navigationTransactions;
  const history = useHistory();
  const { t } = useTranslation();
  const { path } = useRouteMatch();
  const { transactionNumber } = useParams<{ transactionNumber: string }>();
  const total = data?.[0]?.total || 0;
  const transactionNumbers = useMemo(
    () =>
      (data || []).reduce(
        (result, page) => [
          ...result,
          ...page.transactionsIds.map((tr) => tr.transactionNumber),
        ],
        [] as string[]
      ),
    [data]
  );
  const currentTransactionIndex = useMemo(
    () => transactionNumbers.indexOf(transactionNumber),
    [transactionNumber, transactionNumbers]
  );

  const getTransactionUrl = (index: number) =>
    path.replace(
      ":transactionNumber",
      `${transactionNumbers[index]}?navigation=true`
    );

    const isFetchMore = canFetchMore 
    && !isFetchingMore 
    && (!isDirectNavigation || (transactionListInfoResult?.isPresent && !transactionListInfoResultInProgress)) 
    && (transactionNumbers.length - currentTransactionIndex < 5 || currentTransactionIndex === -1);
  useEffect(() => {
    if (isFetchMore) {
      fetchMore();
    }
  }, [
    transactionListInfoResult,
    transactionListInfoResultInProgress,
    fetchMore,
    canFetchMore,
    isFetchingMore,
    transactionNumbers.length, 
    currentTransactionIndex,
  ]);

  if (currentTransactionIndex === -1 || transactionNumbers.length < 2) {
    return null;
  }

  return (
    <div className={styles.transactionNav}>
      <IconButton
        id="previous-transaction-link-button"
        disabled={currentTransactionIndex === 0}
        className={styles.transactionNavButton}
        aria-label={t("transactions.navigation.previous")}
        onClick={() => {
          history.push(getTransactionUrl(currentTransactionIndex - 1));
        }}
      >
        <NavArrowIcon fillColor={getColor("--primary-color")} />
      </IconButton>
      <span>{`${currentTransactionIndex + 1} of ${total}`}</span>
      <IconButton
        id="next-transaction-link-button"
        className={styles.transactionNavButton}
        aria-label={t("transactions.navigation.next")}
        disabled={currentTransactionIndex === transactionNumbers.length - 1}
        onClick={() => {
          history.push(getTransactionUrl(currentTransactionIndex + 1));
        }}
      >
        <NavArrowIcon 
          fillColor={getColor("--primary-color")}
          isRight
         />
      </IconButton>
    </div>
  );
};

export default TransactionNavigation;
